import agentGatewayClient from '@/services/agentGatewayClient'
import pccApi from '@/api/pcc'

const HEARTBEAT_TIMEOUT_MS = 60000

export default {
  state: {
    agentStatus: {
      online: false,
      lastHeartbeatAt: null,
      uptimeSecs: null,
      agentVersion: null,
      otavConnected: false
    },
    playlistState: null,
    schedulerState: null,
    routerState: null,
    routerLabels: { sources: [], destinations: [] },
    inFlightCommands: {},
    connectionState: 'disconnected',
    _stationId: null,
    platforms: [],
    platformsLoading: false,
    platformsError: null
  },

  getters: {
    isAgentOnline(state) {
      if (!state.agentStatus.lastHeartbeatAt) return false
      return Date.now() - state.agentStatus.lastHeartbeatAt < HEARTBEAT_TIMEOUT_MS
    },
    isOtavConnected(state) {
      return state.agentStatus.otavConnected
    },
    commandPending: (state) => (requestId) => {
      return !!state.inFlightCommands[requestId]
    }
  },

  mutations: {
    SET_PCC_STATION_ID(state, stationId) {
      state._stationId = stationId
    },
    SET_PLAYLIST_STATE(state, payload) {
      state.playlistState = payload
    },
    SET_SCHEDULER_STATE(state, payload) {
      state.schedulerState = payload
    },
    SET_ROUTER_STATE(state, payload) {
      state.routerState = payload
    },
    SET_HEARTBEAT(state, payload) {
      state.agentStatus = {
        online: true,
        lastHeartbeatAt: Date.now(),
        uptimeSecs: payload.uptime_secs ?? null,
        agentVersion: payload.agent_version ?? null,
        otavConnected: payload.otav_connected ?? false
      }
    },
    SET_ROUTER_LABELS(state, payload) {
      state.routerLabels = payload
    },
    SET_CONNECTION_STATE(state, connectionState) {
      state.connectionState = connectionState
    },
    SET_IN_FLIGHT_COMMAND(state, { requestId, type, payload }) {
      state.inFlightCommands = { ...state.inFlightCommands, [requestId]: { type, payload, startedAt: Date.now() } }
    },
    RESOLVE_COMMAND(state, requestId) {
      const next = { ...state.inFlightCommands }
      delete next[requestId]
      state.inFlightCommands = next
    },
    SET_AGENT_OFFLINE(state) {
      state.agentStatus = { ...state.agentStatus, online: false }
    },
    SET_PLATFORMS(state, platforms) {
      state.platforms = platforms
    },
    SET_PLATFORMS_LOADING(state, loading) {
      state.platformsLoading = loading
    },
    SET_PLATFORMS_ERROR(state, error) {
      state.platformsError = error
    }
  },

  actions: {
    subscribeToAgentChannel({ commit, dispatch }, stationId) {
      if (!stationId) return
      commit('SET_PCC_STATION_ID', stationId)
      commit('SET_CONNECTION_STATE', 'connecting')

      agentGatewayClient.subscribe(stationId, ({ type, payload, request_id }) => {
        dispatch('handleAgentMessage', { type, payload, request_id })
      })

      commit('SET_CONNECTION_STATE', 'connected')
    },

    unsubscribeFromAgentChannel({ commit }) {
      if (typeof agentGatewayClient._cleanup === 'function') {
        agentGatewayClient._cleanup()
      }
      commit('SET_CONNECTION_STATE', 'disconnected')
      commit('SET_AGENT_OFFLINE')
      commit('SET_PLATFORMS', [])
      commit('SET_PLATFORMS_ERROR', null)
    },

    handleAgentMessage({ commit }, { type, payload, request_id }) {
      switch (type) {
        case 'playlist_state':
          commit('SET_PLAYLIST_STATE', payload)
          break
        case 'scheduler_state':
          commit('SET_SCHEDULER_STATE', payload)
          break
        case 'router_state':
          commit('SET_ROUTER_STATE', payload)
          break
        case 'heartbeat':
          commit('SET_HEARTBEAT', payload)
          break
        case 'router_labels':
          commit('SET_ROUTER_LABELS', payload)
          break
        case 'command_response':
          if (request_id) commit('RESOLVE_COMMAND', request_id)
          break
        default:
          break
      }
    },

    async fetchPlatforms({ commit, state }, { force = false } = {}) {
      // Prevent concurrent in-flight fetches
      if (state.platformsLoading) return
      // Skip if already loaded successfully and not forced (e.g. station switch)
      if (!force && state.platforms.length && !state.platformsError) return
      commit('SET_PLATFORMS_LOADING', true)
      commit('SET_PLATFORMS_ERROR', null)
      try {
        const resp = await pccApi.getPlatforms()
        // index() paginates, so PlatformResource::collection() wraps in data/links/meta
        // regardless of JsonResource::withoutWrapping() (pagination metadata needs a home).
        commit('SET_PLATFORMS', resp.data?.data ?? [])
      } catch (err) {
        commit('SET_PLATFORMS_ERROR', err.message ?? 'Failed to load platforms')
        commit('SET_PLATFORMS', [])
      } finally {
        commit('SET_PLATFORMS_LOADING', false)
      }
    },

    dispatchCommand({ commit, state }, { type, payload = {} }) {
      const stationId = state._stationId
      return new Promise((resolve, reject) => {
        agentGatewayClient
          .dispatchCommand(stationId, type, payload)
          .then(({ request_id }) => {
            if (!request_id) {
              resolve({ success: true })
              return
            }
            commit('SET_IN_FLIGHT_COMMAND', { requestId: request_id, type, payload })
            agentGatewayClient.onCommandResponse(request_id, (response) => {
              commit('RESOLVE_COMMAND', request_id)
              resolve(response)
            })
          })
          .catch(reject)
      })
    }
  }
}
